import { useUploadAvatarImage } from '@/hooks';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { AppRoutes } from '@/utils/types';
import { Presenter, PresenterUsecase } from '@d-id/types';
import { useNavigate } from 'react-router-dom';
import { atom, useRecoilState } from 'recoil';

export const uploadAvatarLoadingAtom = atom({
    key: 'upload-avatar-loading',
    default: false,
});

export interface UseUploadAvatarProps {
    from?: string;
    onUpload?: (avatar?: Presenter) => void;
    useCase?: PresenterUsecase;
    isFromWorkspaceCTA?: boolean;
    inputId?: string;
}

export const useUploadAvatar = ({ from, useCase, onUpload, isFromWorkspaceCTA, inputId }: UseUploadAvatarProps) => {
    const { isAnonymous, loginWithRedirect } = useAuth();
    const mixpanel = useAnalytics();
    const [loading, setLoading] = useRecoilState<boolean>(uploadAvatarLoadingAtom);
    const navigate = useNavigate();

    const { inputEventHandler: onInputChange, onInputFile } = useUploadAvatarImage(from, useCase, avatar => {
        onUpload?.(avatar);
        setLoading(false);
    });

    const onUploadClick = (_: any, toggle?: (state: boolean) => void) => {
        if (isAnonymous) {
            mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-box', isAnonymous });
            loginWithRedirect({ appState: { returnTo: window.location.origin + AppRoutes.PostSso + `?from=${from}` } });
        } else {
            const providedInput = inputId && (document.getElementById(inputId) as HTMLInputElement);

            const fileInput = providedInput || document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = 'image/*';
            fileInput.oninput = (e: any) => {
                if (isFromWorkspaceCTA) {
                    navigate(AppRoutes.Avatars);
                }

                toggle?.(true);
                setLoading(true);
                onInputChange(e, () => {
                    toggle?.(false);
                    setLoading(false);
                });
            };
            fileInput.click();
            if (!providedInput) {
                fileInput.remove();
            }
        }
    };

    const onGetFile = (file: any) => {
        if (isFromWorkspaceCTA) {
            navigate(AppRoutes.Avatars);
        }
        setLoading(true);
        onInputFile(file, () => {
            setLoading(false);
        });
    };

    return {
        loading,
        onUploadClick,
        onGetFile,
    };
};
